import { useEffect, useRef } from "react";

interface NetworkBackgroundProps {
  className?: string;
}

// Cable types for visual variety
type CableType = 'fiber' | 'copper';

interface Cable {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  type: CableType;
  pulseOffset: number;
  pulseSpeed: number;
  controlPoints: { x: number; y: number }[];
}

interface DataPacket {
  cable: Cable;
  progress: number;
  speed: number;
  color: string;
}

const NetworkBackground = ({ className = "" }: NetworkBackgroundProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationFrameId: number;
    let particles: Particle[] = [];
    let cables: Cable[] = [];
    let dataPackets: DataPacket[] = [];
    let time = 0;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    class Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      radius: number;
      isFiberNode: boolean;

      constructor() {
        this.x = Math.random() * canvas!.width;
        this.y = Math.random() * canvas!.height;
        this.vx = (Math.random() - 0.5) * 0.5;
        this.vy = (Math.random() - 0.5) * 0.5;
        this.radius = Math.random() * 2 + 1;
        this.isFiberNode = Math.random() > 0.7;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;

        if (this.x < 0 || this.x > canvas!.width) this.vx *= -1;
        if (this.y < 0 || this.y > canvas!.height) this.vy *= -1;
      }

      draw() {
        if (!ctx) return;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        
        if (this.isFiberNode) {
          // Fiber node - glowing cyan
          const gradient = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.radius * 2);
          gradient.addColorStop(0, "rgba(0, 255, 255, 0.9)");
          gradient.addColorStop(0.5, "rgba(0, 200, 255, 0.5)");
          gradient.addColorStop(1, "rgba(0, 150, 255, 0)");
          ctx.fillStyle = gradient;
        } else {
          // Regular node - blue
          ctx.fillStyle = "rgba(59, 130, 246, 0.6)";
        }
        ctx.fill();
      }
    }

    const initParticles = () => {
      particles = [];
      const particleCount = Math.floor((canvas.width * canvas.height) / 15000);
      for (let i = 0; i < Math.min(particleCount, 100); i++) {
        particles.push(new Particle());
      }
    };

    const initCables = () => {
      cables = [];
      const cableCount = 8;
      
      for (let i = 0; i < cableCount; i++) {
        const type: CableType = Math.random() > 0.5 ? 'fiber' : 'copper';
        const startEdge = Math.floor(Math.random() * 4);
        let startX: number, startY: number, endX: number, endY: number;
        
        // Start from edges
        switch (startEdge) {
          case 0: // top
            startX = Math.random() * canvas.width;
            startY = -20;
            break;
          case 1: // right
            startX = canvas.width + 20;
            startY = Math.random() * canvas.height;
            break;
          case 2: // bottom
            startX = Math.random() * canvas.width;
            startY = canvas.height + 20;
            break;
          default: // left
            startX = -20;
            startY = Math.random() * canvas.height;
        }
        
        // End towards center area
        endX = canvas.width * 0.3 + Math.random() * canvas.width * 0.4;
        endY = canvas.height * 0.3 + Math.random() * canvas.height * 0.4;
        
        // Generate curved control points
        const controlPoints = [];
        const segments = 3 + Math.floor(Math.random() * 3);
        for (let j = 1; j < segments; j++) {
          const t = j / segments;
          controlPoints.push({
            x: startX + (endX - startX) * t + (Math.random() - 0.5) * 100,
            y: startY + (endY - startY) * t + (Math.random() - 0.5) * 100
          });
        }
        
        cables.push({
          startX,
          startY,
          endX,
          endY,
          type,
          pulseOffset: Math.random() * Math.PI * 2,
          pulseSpeed: 0.02 + Math.random() * 0.03,
          controlPoints
        });
      }
    };

    const initDataPackets = () => {
      dataPackets = [];
      cables.forEach(cable => {
        const packetCount = 2 + Math.floor(Math.random() * 3);
        for (let i = 0; i < packetCount; i++) {
          dataPackets.push({
            cable,
            progress: Math.random(),
            speed: cable.type === 'fiber' ? 0.008 + Math.random() * 0.006 : 0.003 + Math.random() * 0.003,
            color: cable.type === 'fiber' 
              ? `hsl(${180 + Math.random() * 40}, 100%, 70%)` 
              : `hsl(${30 + Math.random() * 20}, 80%, 60%)`
          });
        }
      });
    };

    const getCablePoint = (cable: Cable, t: number) => {
      const points = [
        { x: cable.startX, y: cable.startY },
        ...cable.controlPoints,
        { x: cable.endX, y: cable.endY }
      ];
      
      // Catmull-Rom spline interpolation for smooth curves
      const n = points.length - 1;
      const segment = Math.min(Math.floor(t * n), n - 1);
      const localT = (t * n) - segment;
      
      const p0 = points[Math.max(0, segment - 1)];
      const p1 = points[segment];
      const p2 = points[Math.min(n, segment + 1)];
      const p3 = points[Math.min(n, segment + 2)];
      
      const t2 = localT * localT;
      const t3 = t2 * localT;
      
      return {
        x: 0.5 * ((2 * p1.x) + (-p0.x + p2.x) * localT + (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 + (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3),
        y: 0.5 * ((2 * p1.y) + (-p0.y + p2.y) * localT + (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 + (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3)
      };
    };

    const drawCable = (cable: Cable) => {
      if (!ctx) return;
      
      const isFiber = cable.type === 'fiber';
      const pulseIntensity = Math.sin(time * cable.pulseSpeed + cable.pulseOffset) * 0.3 + 0.7;
      
      // Draw cable path
      ctx.beginPath();
      ctx.moveTo(cable.startX, cable.startY);
      
      for (let t = 0; t <= 1; t += 0.02) {
        const point = getCablePoint(cable, t);
        ctx.lineTo(point.x, point.y);
      }
      
      if (isFiber) {
        // Fiber optic - glowing cyan/blue gradient
        const gradient = ctx.createLinearGradient(cable.startX, cable.startY, cable.endX, cable.endY);
        gradient.addColorStop(0, `rgba(0, 255, 255, ${0.15 * pulseIntensity})`);
        gradient.addColorStop(0.5, `rgba(100, 200, 255, ${0.25 * pulseIntensity})`);
        gradient.addColorStop(1, `rgba(0, 150, 255, ${0.15 * pulseIntensity})`);
        ctx.strokeStyle = gradient;
        ctx.lineWidth = 3;
        ctx.lineCap = 'round';
        ctx.stroke();
        
        // Inner glow for fiber
        ctx.strokeStyle = `rgba(200, 255, 255, ${0.4 * pulseIntensity})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      } else {
        // Copper cable - orange/gold gradient
        const gradient = ctx.createLinearGradient(cable.startX, cable.startY, cable.endX, cable.endY);
        gradient.addColorStop(0, `rgba(255, 140, 50, ${0.2 * pulseIntensity})`);
        gradient.addColorStop(0.5, `rgba(255, 180, 80, ${0.3 * pulseIntensity})`);
        gradient.addColorStop(1, `rgba(200, 120, 40, ${0.2 * pulseIntensity})`);
        ctx.strokeStyle = gradient;
        ctx.lineWidth = 2.5;
        ctx.lineCap = 'round';
        ctx.stroke();
        
        // Copper sheen
        ctx.strokeStyle = `rgba(255, 200, 150, ${0.2 * pulseIntensity})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    };

    const drawDataPacket = (packet: DataPacket) => {
      if (!ctx) return;
      
      const point = getCablePoint(packet.cable, packet.progress);
      const isFiber = packet.cable.type === 'fiber';
      
      // Glowing data packet
      const gradient = ctx.createRadialGradient(point.x, point.y, 0, point.x, point.y, isFiber ? 8 : 5);
      gradient.addColorStop(0, packet.color);
      gradient.addColorStop(0.5, isFiber ? 'rgba(0, 255, 255, 0.5)' : 'rgba(255, 180, 100, 0.5)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      
      ctx.beginPath();
      ctx.arc(point.x, point.y, isFiber ? 8 : 5, 0, Math.PI * 2);
      ctx.fillStyle = gradient;
      ctx.fill();
      
      // Bright center
      ctx.beginPath();
      ctx.arc(point.x, point.y, isFiber ? 3 : 2, 0, Math.PI * 2);
      ctx.fillStyle = isFiber ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 220, 180, 0.9)';
      ctx.fill();
    };

    const drawConnections = () => {
      if (!ctx) return;
      const maxDistance = 150;

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < maxDistance) {
            const opacity = (1 - distance / maxDistance) * 0.4;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            
            // Different colors for fiber nodes
            if (particles[i].isFiberNode || particles[j].isFiberNode) {
              ctx.strokeStyle = `rgba(0, 200, 255, ${opacity * 0.8})`;
            } else {
              ctx.strokeStyle = `rgba(59, 130, 246, ${opacity})`;
            }
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
      }
    };

    const animate = () => {
      if (!ctx) return;
      time += 1;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw gradient background
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      gradient.addColorStop(0, "#0f172a");
      gradient.addColorStop(0.5, "#1e293b");
      gradient.addColorStop(1, "#0f172a");
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw cables first (background layer)
      cables.forEach(drawCable);
      
      // Draw data packets on cables
      dataPackets.forEach(packet => {
        packet.progress += packet.speed;
        if (packet.progress > 1) {
          packet.progress = 0;
        }
        drawDataPacket(packet);
      });

      // Update and draw particles
      particles.forEach((particle) => {
        particle.update();
        particle.draw();
      });

      drawConnections();
      animationFrameId = requestAnimationFrame(animate);
    };

    resizeCanvas();
    initParticles();
    initCables();
    initDataPackets();
    animate();

    const handleResize = () => {
      resizeCanvas();
      initParticles();
      initCables();
      initDataPackets();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={`fixed inset-0 -z-10 ${className}`}
      style={{ pointerEvents: "none" }}
    />
  );
};

export default NetworkBackground;
